import { useCallback, useEffect, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';

import { getStaffRequest } from '@vyce/core/src/api/companies';

import { DEFAULT_ROLES } from '../constants';

export interface StaffItem {
  uuid: string;
  name: string;
}

interface UseStaffSearchProps {
  companyId?: string;
  roles?: string[];
  getStaff?: typeof getStaffRequest;
}

export const useStaffSearch = ({
  companyId,
  roles = DEFAULT_ROLES,
  getStaff = getStaffRequest,
}: UseStaffSearchProps) => {
  const [staff, setStaff] = useState<StaffItem[]>([]);
  const [substring, setSubstring] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchStaff = useCallback(
    async (searchSubstring: string) => {
      if (!companyId) return;
      try {
        setLoading(true);
        const { data } = await getStaff(companyId, {
          limit: 1000,
          offset: 0,
          substring: searchSubstring,
          roles,
        });
        setStaff(
          data.items.map((item: { user_id: string; first_name: string; last_name: string }) => ({
            uuid: item.user_id,
            name: `${item.first_name} ${item.last_name}`,
          }))
        );
      } catch (e) {
        setStaff([]);
      } finally {
        setLoading(false);
      }
    },
    [companyId, roles, getStaff]
  );

  const debouncedFetchStaff = useMemo(() => debounce(fetchStaff, 500), [fetchStaff]);

  useEffect(() => {
    debouncedFetchStaff(substring);
    return () => debouncedFetchStaff.cancel();
  }, [substring, debouncedFetchStaff]);

  return {
    staff,
    setSubstring,
    loading,
  };
};
